import type { EducationalFacilityDTO, ForeignLanguageDTO, MeasureDTO } from '@ui/shared/util-api';
import { MapItem } from '@ui/shared/util-map';
import type { EducationalBranch } from './educational-branch';
import { statusCodes } from './status-codes';
import type { EducationalFundingMeasureGroup } from './educational-funding-measure-group';

const formatMeasureCost = (measure: MeasureDTO): string => {
    if (!measure.costs) {
        return '';
    }

    const formatter = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
    const million = 1000000;

    return formatter.format(measure.costs * million);
};

// Group educational funding measures by status
const buildMeasureGroups = (measures: Array<MeasureDTO> | undefined): Array<EducationalFundingMeasureGroup> =>
    measures?.reduce(
        (groups, measure) => {
            const processedMeasure = {
                type: measure.type.id,
                description: measure.short_description ?? '',
                cost: formatMeasureCost(measure),
                status: statusCodes[measure.status] ?? '',
            };

            const existingGroup = groups.find(group => group.status === processedMeasure.status);

            if (existingGroup) {
                existingGroup.measures.push(processedMeasure);
            } else {
                groups.push({
                    status: processedMeasure.status,
                    measures: [processedMeasure],
                });
            }

            return groups;
        },
        new Array<EducationalFundingMeasureGroup>(),
    ) ?? [];

const buildSortedLanguageList = (languages: Array<ForeignLanguageDTO> | undefined): Array<string> => {
    if (!languages) {
        return [];
    }

    /*
     * Check if the languages have more than one position among them.
     * If all languages have the same position, the position is not displayed and the languages are sorted by name instead.
     */
    const areLanguagesSorted = Array.from(new Set(languages.map(language => language.position))).length > 1;

    if (areLanguagesSorted) {
        return languages
            .sort((a, b) => a.position - b.position)
            .map(language => `${ language.position }. ${ language.language_name }`);
    } else {
        return languages
            .sort((a, b) =>
                (a.language_name === b.language_name
                    ? 0
                    : a.language_name > b.language_name
                        ? 1
                        : -1),
            )
            .map(language => language.language_name);
    }
};

export class SchoolMapItem extends MapItem {
    public readonly typeLabel: string;
    public readonly imagePath: string;
    public readonly placeholderPath?: string;
    public readonly thumbnailPath: string;
    public readonly accessibilityFeatures: Array<string>;
    public readonly administrationContact: string;
    public readonly specialFeatures: string;
    public readonly careServices: Array<string>;
    public readonly weblink: string;
    public readonly unlinkedLanguages: Array<string>;
    public readonly educationalBranches: Array<EducationalBranch>;
    public readonly educationalBranchesLabel: string;
    public readonly doEducationalBranchesHaveLanguages: boolean;
    public readonly measureGroups: Array<EducationalFundingMeasureGroup>;

    constructor(mapItem: EducationalFacilityDTO, data?: { measures?: Array<MeasureDTO> }) {
        super(
            mapItem,
            `id-${ mapItem.id }-type-school`,
        );

        this.iconPath = mapItem.icon ?? '';
        this.typeLabel = mapItem.type.name;
        this.iconLabel = this.typeLabel ? `Schultyp: ${ this.typeLabel }` : '';
        this.imagePath = mapItem.image?.thumbs?.detail ?? mapItem.image?.image ?? '';
        this.thumbnailPath = mapItem.image?.thumbs?.preview ?? this.imagePath;
        this.accessibilityFeatures = mapItem.accessibility_features.map(feature => feature.name);
        this.administrationContact = mapItem.administration ?? '';
        this.specialFeatures = mapItem.peculiarities ?? '';
        this.careServices = mapItem.services.map(service => service.name);
        this.weblink = mapItem.website ?? '';
        this.unlinkedLanguages = buildSortedLanguageList(mapItem.unlinked_foreign_languages);
        this.educationalBranches = mapItem.branches.map(branch => ({
            name: branch.name,
            code: branch.short_code,
            languages: buildSortedLanguageList(branch.foreign_languages),
        }));
        this.educationalBranchesLabel = mapItem.type.branches_label;
        this.doEducationalBranchesHaveLanguages = this.educationalBranches.some(branch => !!branch.languages.length);
        this.measureGroups = buildMeasureGroups(data?.measures);
    }
}
